import SortableRow from '@components/SortableRow'
import React from 'react'
import { DraftDestination } from './core/draft'
import { daysLabel } from './core/presentation'
import { DayCount, PlusButton, StepperButton } from './WhereStep.styles'
import { TestIds } from '@/testIds'

/** What the edit flow adds to a row: what it holds, and what its edit would break. */
export interface DestinationRowDetail {
	/** A line under the name — "3 events". */
	meta?: string
	/** An error under the band — the row's days still hold events the edit would strand. */
	error?: string
	/** Why Remove is refused for this row — the destination still holds events. Unset lets it go. */
	removeRefusal?: string
}

interface DestinationRowProps extends Omit<DestinationRowDetail, 'removeRefusal'> {
	destination: DraftDestination
	onIncrement: () => void
	onDecrement: () => void
	onRemove: () => void
}

/**
 * One destination in the Where step: the shared sortable row — its name,
 * remove, and the drag handle that reorders it — with the day stepper as the
 * row's own controls. The edit flow adds a meta line and, when the row's edit
 * can't be saved, an error under it.
 */
const DestinationRow: React.FC<DestinationRowProps> = ({
	destination,
	meta,
	error,
	onIncrement,
	onDecrement,
	onRemove,
}) => {
	const name = destination.place.name

	return (
		<SortableRow
			id={destination.key}
			name={name}
			meta={meta}
			error={error}
			removeLabel={`Remove ${name}`}
			reorderLabel={`Reorder ${name}`}
			onRemove={onRemove}
			testId={TestIds.tripCreate.destination(name)}
			controls={
				<>
					<StepperButton
						type="button"
						aria-label={`Fewer days in ${name}`}
						disabled={destination.dayCount <= 1}
						onClick={onDecrement}
					>
						−
					</StepperButton>
					<DayCount>{daysLabel(destination.dayCount)}</DayCount>
					<PlusButton type="button" aria-label={`More days in ${name}`} onClick={onIncrement}>
						+
					</PlusButton>
				</>
			}
		/>
	)
}

export default DestinationRow
